<section class="section section--paper popband" id="popularity">
  <div class="wrap">
    <div class="sec-head pop-head">
      <div>
        <span class="eyebrow">Popularity is not value</span>
        <h2 class="h-sec">Rank tells you how many people share a name.</h2>
      </div>
      <!-- the ranks are stated, not characterised, so the sentence cannot go
           stale if the underlying data moves -->
      <p class="lead pop-lead" *ngIf="pair.length === 2">
        {{ pair[0].name }} is the {{ ordinal(pair[0].rank) }} most common name in England,
        {{ pair[1].name }} the {{ ordinal(pair[1].rank) }} — one place apart. Their registrations
        sold for <b>{{ moneyFull(pair[0].shownPence) }}</b> and
        <b>{{ moneyFull(pair[1].shownPence) }}</b>. Rank tells you how many people share a name,
        and nothing at all about what its plate is worth.
      </p>
    </div>

    <p class="pop-src" *ngIf="data">
      {{ data.total | number }} verified sales from the PlateX record, {{ data.yearRange[0] }} to
      {{ data.yearRange[1] }}. Position is how many people in England share the name, counted over the living
      population rather than among recent births, on a log scale from 1st to 1000th.
      <ng-container *ngIf="data.coverage?.verifiedSales as all">The ranking covers England’s
      1,000 most common forenames, so of {{ all | number }} verified sales the
      {{ all - data.total | number }} whose name falls outside it cannot be placed here.</ng-container>
    </p>

    <div class="pop-loading" *ngIf="loading">Loading the sale record…</div>
    <div class="pop-error" *ngIf="failed">The evidence band could not be loaded.</div>

    <p class="plot-basis" *ngIf="!loading && !failed" aria-live="polite">
      Prices {{ basisLabel }}<span class="pb-hint"> · change the basis below the chart</span>
    </p>

    <!-- the chart: cloud on canvas, standouts as real elements over it -->
    <div class="plot" #plot *ngIf="!loading && !failed">
      <canvas #cloud class="cloud" aria-hidden="true"></canvas>
      <svg class="axes" [attr.viewBox]="'0 0 ' + box.w + ' ' + box.h" *ngIf="box.w">
        <g class="grid">
          <line *ngFor="let t of yTicks" [attr.x1]="box.l" [attr.x2]="box.w - box.r"
                [attr.y1]="t.y" [attr.y2]="t.y"/>
        </g>
        <g class="axis-y">
          <text *ngFor="let t of yTicks" [attr.x]="box.l - 12" [attr.y]="t.y + 4">{{ t.label }}</text>
        </g>
        <g class="axis-x">
          <text *ngFor="let t of xTicks" [attr.x]="t.x" [attr.y]="box.h - 18">{{ t.label }}</text>
        </g>
      </svg>
      <!-- labelled standouts -->
      <a class="pt" *ngFor="let p of labelled; trackBy: trackByReg"
         [routerLink]="plateLink(p.registration)"
         [style.left.px]="xPos(p.rank)" [style.top.px]="yPos(p.shownPence)"
         [attr.aria-label]="p.name + ', ' + ordinal(p.rank) + ' most common name in England, ' + p.display + ' sold ' + moneyFull(p.shownPence) + ' in ' + p.year">
        <span class="dot" [class.approx]="p.approx" [class.fmt]="p.formatValue"></span>
        <span class="lb">{{ p.name }}<span class="lb-p">{{ moneyFull(p.shownPence) }}</span></span>
      </a>
      <span class="x-cap">How many people share the name →</span>
    </div>

    <!-- the confound, stated rather than left for the reader to fall into -->
    <p class="pop-fmt" *ngIf="!loading && !failed && formatCount">
      <span class="key"><i class="k-solid"></i>priced as a name</span>
      <span class="key"><i class="k-hollow"></i>priced as a format</span>
      {{ formatCount | number }} of these sales are priced by the registration rather than by the
      name: it reads as a number (101 O is 1010, 6 OO is 600), it is a Number 1 plate, or it is
      under four characters. Length alone is not the test, so 81 LL stays credited to the name,
      because it is unambiguously Bill. They are kept in because they are real sales, and drawn
      hollow because the price is not the name’s.
    </p>

    <!-- basis: named, never applied silently -->
    <div class="pop-controls" *ngIf="!loading && !failed">
      <div class="tb-group" role="group" aria-label="Price basis">
        <button [class.active]="basis === 'nominal'" (click)="setBasis('nominal')">As sold</button>
        <button [class.active]="basis === 'cpi'" (click)="setBasis('cpi')">Today’s money</button>
      </div>
      <p class="pop-basis" aria-live="polite">{{ basisNote }}</p>
    </div>

    <!-- the same evidence, readable -->
    <div class="pop-table" *ngIf="!loading && !failed">
      <table>
        <thead>
          <tr>
            <th><button (click)="sortBy('reg')" [class.on]="sortKey === 'reg'">Registration</button></th>
            <th><button (click)="sortBy('rank')" [class.on]="sortKey === 'rank'">Popularity</button></th>
            <th><button (click)="sortBy('price')" [class.on]="sortKey === 'price'">Sale</button></th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let p of sortedRows; trackBy: trackByReg">
            <td>
              <a [routerLink]="plateLink(p.registration)" class="plate-a">
                <span class="plate sm"><span class="ch">{{ p.display }}</span></span>
              </a>
              <span class="t-nm">{{ p.name }}<span class="t-fmt" *ngIf="p.formatValue"
                [attr.title]="'Priced by the registration, not the name: ' + p.formatReason">{{ p.formatReason }}</span></span>
            </td>
            <td class="t-rank">{{ ordinal(p.rank) }}</td>
            <td class="t-price">
              {{ moneyFull(p.shownPence) }}
              <span class="t-yr">({{ p.year }}<span *ngIf="p.approx" title="No inflation figure for this year, so shown as sold">, as sold</span>)</span>
            </td>
          </tr>
        </tbody>
      </table>
      <div class="pop-more" *ngIf="hasMoreRows">
        <button class="btn btn--ghost" (click)="showMore()">Show more sales</button>
        <span class="lm-note">{{ sortedRows.length }} of {{ plotted.length | number }}</span>
      </div>
    </div>

    <p class="pop-foot" *ngIf="data">
      Prevalence: <a *ngIf="data.metadata.source.sourceUrl" class="src-link"
        [href]="data.metadata.source.sourceUrl" target="_blank"
        rel="noopener noreferrer">{{ data.metadata.source.source }}</a><span
        *ngIf="!data.metadata.source.sourceUrl">{{ data.metadata.source.source }}</span>,
      which publishes no reference year. Sales are hammer-verified only.
    </p>
  </div>
</section>
